import { SalesOrderDTO, Permission, DocumentType } from 'core/api';
import closeDocument from '../helpers/document-creation/showCloseDocumentScreen';
import * as TakeOutOrderStore from './TakeOutOrderStore';
import { Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import { CashdrawerFacade } from 'pos/facades';
import { ExternalPaymentsService, WhenPrintService } from 'pos/services';
import { PaymentDTOFactory } from '../helpers/payments-processing';
import { Message } from 'core/ui-next/modal';

declare global {
	interface CommandType {
		closeTakeOutOrder: (order: SalesOrderDTO) => Promise<Result>
	}
}

async function closeWithPreferredPaymentMethod(order: SalesOrderDTO): Promise<Result> {

	// Esto está medio copiado de doDirectPayment
	const payment = PaymentDTOFactory.create(await getPreferredPaymentMethod(order), order.PendingAmount);
	const paymentsResult = await CashdrawerFacade.checkPayments([payment], order.PendingAmount, false);
	if (paymentsResult.ErrorMessage) {
		await Message.show(paymentsResult.ErrorMessage);
		return Result.error();
	}

	const externalPayments = ExternalPaymentsService.forPay('PrePay', order.TicketGlobalId);

	const result = await externalPayments.register(payment);
	if (!result.ok)
		return Result.error();

	const printTicket = await WhenPrintService.shouldPrintInvoice(DocumentType.BasicInvoice);

	const cashPaymentResult = await externalPayments.commitCashPayments(0);
	if (!cashPaymentResult.ok)
		return Result.error();

	const closeResult = await TakeOutOrderStore.close(order.Id, {
		InvoiceType: DocumentType.BasicInvoice,
		Payments: [payment],
		PrintTicket: printTicket,
		PrintGiftTicket: false,
		RelatedInvoiceId: null,
		BuyerCustomer: order.Customer
	});

	if (!closeResult.ok)
		await externalPayments.undoAll();

	return result;
}

async function getPreferredPaymentMethod(order: SalesOrderDTO) {
	const operation = await CashdrawerFacade.createPaymentOperation();
	const method = operation.Methods.find(x => x.Id === order.PreferredPaymentMethodId);
	if (!method) {
		throw Error(`La forma de pago ${order.PreferredPaymentMethodId}/${order.PreferredPaymentMethodName} asociada al pedido ya no existe`);
	}
	return method;
}


function closeSelectingPaymentMethods(order: SalesOrderDTO): Promise<Result> {
	return closeDocument({
		layout: 'CloseSalesOrder',
		getDocument: () => {
			return {
				ticketIds: [order.TicketId],
				ticketGlobalId: order.TicketGlobalId,
				customer: order.Customer,
				pendingAmount: order.PendingAmount
			};
		},
		setCustomer: async _ => { },
		closeDocument: async params => {
			return await TakeOutOrderStore.close(order.Id, {
				InvoiceType: params.InvoiceType,
				Payments: params.Payments,
				PrintTicket: params.PrintTicket,
				PrintGiftTicket: params.PrintGiftTicket,
				RelatedInvoiceId: null,
				BuyerCustomer: params.BuyerCustomer
			});
		}
	});
}

Command.register('closeTakeOutOrder', Preconditions.and(Preconditions.ensure(Permission.Ticket_Close), Preconditions.ensureAllowCashTransactions, Preconditions.ensureIsNotLite), async (order: SalesOrderDTO): Promise<Result> => {

	// En el PC esto pasa por el SetDocumentPaymentsPresenter que es capaz de hacer tanto cobro rápido, como manual.
	// Aquí eso está separado en dos Commands, uno que se ejecuta a través del DirectActionCommand y otro de cerrar.
	// De momento dejo aquí el código que distingue los dos casos, aunque no me acaba de
	// gustar.

	if (order.PreferredPaymentMethodId)
		await closeWithPreferredPaymentMethod(order);
	else
		await closeSelectingPaymentMethods(order);

	return Result.ok();
});
